import type { SearchMatch } from '@vivport/backend/types';
import { ExploreMatchLine } from './ExploreMatchLine';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Speak2MePlayControl } from '@/components/widget/speak2me-play-control';
import { TagIndicator } from '@/components/widget/tag-list';
import { useToggleStar } from '@/hooks';
import { useContentDisplayTitle } from '@/hooks/content';
import { cn } from '@/lib/utils';
import { useSelectedItemStore } from '@/store/selectedItemStore.ts';
import { formatDate } from '@/utils/dateUtils';
import { Content } from '@vivport/backend/types';
import { getContentTypeIcon } from '@/lib/content-labels';
import { Star } from 'lucide-react';
import { ContentExternalRefChip } from '@/components/widget/content-external-ref-chip';
import { ObligationBadge } from '@/components/widget/obligation-badge';
import { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import { buildContentParticipantsLabel, isContentPrivate, resolveRowTagIds } from './ExploreEntryHelpers';

interface ListEntryProps {
  content: Content;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  onClick?: () => void;
  match?: SearchMatch;
  /** When set, a play control is shown instead of the creator avatar; opens Speak2Me for this Neo chat. */
  onOpenInSpeak2Me?: () => void;
}

const buildMessageSubtitle = (
  content: Extract<Content, { contentType: 'MESSAGE' }>,
  displayTitle?: string
): string | null => {
  const sender = content.from?.name?.trim() || content.from?.email?.trim() || '';
  const subject = content.subject?.trim() || '';
  const snippet = content.snippet?.trim() || '';
  const normalizedDisplayTitle = displayTitle?.trim().toLowerCase() || '';
  const subjectForSubtitle =
    subject && subject.toLowerCase() === normalizedDisplayTitle ? '' : subject;

  const parts = [sender, subjectForSubtitle, snippet].filter((part, index, array) => {
    if (!part) {
      return false;
    }
    return array.findIndex((candidate) => candidate.toLowerCase() === part.toLowerCase()) === index;
  });

  return parts.length > 0 ? parts.join(' - ') : null;
};

export function ListEntry({
  content,
  checked,
  onCheckedChange,
  onClick,
  match,
  onOpenInSpeak2Me,
}: ListEntryProps) {
  const toggleStarMutation = useToggleStar();
  const isStarredFromTracking = content.tracking?.isStarred || false;
  const [isStarred, setIsStarred] = useState(isStarredFromTracking);
  const isUnread = content.tracking?.isRead !== true;

  useEffect(() => {
    setIsStarred(content.tracking?.isStarred || false);
  }, [content.tracking?.isStarred]);

  const handleStarredChange = async (starred: boolean) => {
    setIsStarred(starred);
    toggleStarMutation.mutate({ contentId: content.id, isStarred: starred });
  };

  // Extract display data from content
  const displayTitle = useContentDisplayTitle(content);
  const subtitle =
    content.contentType === 'MESSAGE'
      ? (buildMessageSubtitle(content, displayTitle) ?? (content.description || null))
      : content.description || null;
  const date = new Date(content.timelineAt || content.originCreatedAt);
  const isPrivate = isContentPrivate(content);
  const participantsLabel = buildContentParticipantsLabel(content);
  const tagIds = resolveRowTagIds(content);
  const ContentIcon = getContentTypeIcon(content.contentType);
  const contentTypeIcon = (
    <ContentIcon className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
  );
  const templateStatusChip =
    content.contentType === 'TEMPLATE' ? (
      <Badge variant={content.status === 'PUBLISHED' ? 'default' : 'secondary'} className="shrink-0">
        {content.status === 'DRAFT' ? 'Draft' : content.status === 'PUBLISHED' ? 'Published' : 'Retired'}
      </Badge>
    ) : null;
  const externalRefChip =
    content.contentType === 'FILE' ? <ContentExternalRefChip externalRefs={content.externalRefs} /> : null;
  const obligationIcon = <ObligationBadge contentId={content.id} rollup={content.obligationRollup} reserveSpace />;

  // Handle the main click on the list item
  const handleMainClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Don't trigger navigation if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[role="checkbox"]')) {
      return;
    }
    // Guard against any nested anchor default navigation (e.g., markdown links).
    if (target.closest('a')) {
      return;
    }
    onClick?.();
  };

  return (
    <li
      data-testid="explore-entry"
      data-content-type={content.contentType}
      data-neo-chat-entry={content.contentType === 'NEO_CHAT' ? 'true' : undefined}
      className={cn(
        'search-result-item w-full overflow-hidden px-4 transition-colors',
        isUnread ? 'bg-background hover:bg-muted/50' : 'bg-muted/50 hover:bg-muted'
      )}
    >
      <div className="sr-only">
        <span data-testid="explore-entry-content-type">{content.contentType}</span>
        {content.contentType === 'NEO_CHAT' ? (
          <span data-testid="explore-entry-neo-chat">{content.id}</span>
        ) : null}
        <span data-testid="explore-entry-title">{displayTitle}</span>
        <span data-testid="explore-entry-subtitle">{subtitle ?? ''}</span>
      </div>
      {/* Mobile layout (default - small screens) */}
      <div className="flex py-3 sm:hidden">
        {/* Left side - Vertically centered checkbox */}
        <div className="flex items-center pr-3">
          <Checkbox checked={checked} onClick={(e) => e.stopPropagation()} onCheckedChange={onCheckedChange} />
        </div>
        
        {/* Right side - Content in rows */}
        <div className="flex-1 min-w-0 space-y-2">
          {/* First row: Star, parties, media icons, and date */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                className="focus:outline-none"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStarredChange(!isStarred);
                }}
              >
                <Star
                  className={`h-4 w-4 ${isStarred ? 'text-highlight-strong fill-highlight-strong' : 'text-muted-foreground'} transition-colors`}
                />
              </button>
              {contentTypeIcon}
              {obligationIcon}
              <TagIndicator tagIds={tagIds} />
              {onOpenInSpeak2Me ? (
                <Speak2MePlayControl onOpen={onOpenInSpeak2Me} />
              ) : isPrivate ? (
                <span className="text-sm truncate min-w-0 text-muted-foreground">Private</span>
              ) : (
                <span
                  className={cn(
                    'text-sm truncate min-w-0',
                    isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
                  )}
                >
                  {participantsLabel}
                </span>
              )}
            </div>
            <time 
              className="text-sm text-muted-foreground whitespace-nowrap cursor-pointer"
              onClick={handleMainClick}
            >
              {formatDate(date)}
            </time>
          </div>
          
          {/* Second row: Title */}
          <div
            className="cursor-pointer"
            onClick={handleMainClick}
          >
            <h3
              className={cn(
                'text-sm truncate',
                isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
              )}
            >
              {displayTitle}
            </h3>
            {templateStatusChip}
            {externalRefChip}
          </div>

          {/* Third row: Subtitle */}
          {subtitle && (
            <div 
              className="cursor-pointer"
              onClick={handleMainClick}
            >
              <div className="text-sm text-muted-foreground truncate">
                <Markdown
                  className="line-clamp-1"
                  components={{
                    // Prevent markdown links in preview snippets from hijacking row navigation.
                    a: ({ children }) => <span>{children}</span>
                  }}
                >
                  {subtitle}
                </Markdown>
              </div>
            </div>
          )}
          {match ? (
            <div className="cursor-pointer" onClick={handleMainClick}>
              <ExploreMatchLine match={match} />
            </div>
          ) : null}
        </div>
      </div>

      {/* Desktop layout (sm and above - original layout) */}
      <div className="hidden min-w-0 sm:flex items-center space-x-4">
        <div className="flex items-center space-x-2 py-3">
          <Checkbox checked={checked} onClick={(e) => e.stopPropagation()} onCheckedChange={onCheckedChange} />
          <button
            className="focus:outline-none"
            onClick={(e) => {
              e.stopPropagation();
              handleStarredChange(!isStarred);
            }}
          >
            <Star
              className={`h-4 w-4 ${isStarred ? 'text-highlight-strong fill-highlight-strong' : 'text-muted-foreground'} transition-colors`}
            />
          </button>
          {contentTypeIcon}
          {obligationIcon}
          <TagIndicator tagIds={tagIds} />
        </div>
        <div
          className="flex w-44 min-w-0 shrink-0 items-center cursor-pointer py-3"
          onClick={handleMainClick}
        >
          {onOpenInSpeak2Me ? (
            <Speak2MePlayControl onOpen={onOpenInSpeak2Me} />
          ) : isPrivate ? (
            <span className="truncate text-sm text-muted-foreground">Private</span>
          ) : (
            <span
              className={cn(
                'truncate text-sm',
                isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
              )}
            >
              {participantsLabel}
            </span>
          )}
        </div>
        <div
          className="flex-1 min-w-0 space-y-1 sm:space-y-0 cursor-pointer py-3"
          onClick={handleMainClick}
        >
          <div className="flex min-w-0 flex-col sm:flex-row sm:items-center">
            <div className="flex min-w-0 items-center gap-2 flex-wrap">
              <h3
                className={cn(
                  'min-w-0 text-sm truncate',
                  isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
                )}
              >
                {displayTitle}
              </h3>
              {templateStatusChip}
              {externalRefChip}
            </div>
            <div className="min-w-0 text-sm text-muted-foreground truncate sm:ml-2 sm:flex-1 flex items-center">
              {subtitle && (
                <div className="flex min-w-0 items-center">
                  <p className="hidden sm:inline"> -&nbsp;</p>
                  <Markdown
                    className="line-clamp-1 min-w-0 break-words"
                    components={{
                      // Prevent markdown links in preview snippets from hijacking row navigation.
                      a: ({ children }) => <span>{children}</span>
                    }}
                  >
                    {subtitle}
                  </Markdown>
                </div>
              )}
            </div>
          </div>
          {match ? <ExploreMatchLine match={match} /> : null}
        </div>
        <div
          className="flex items-center gap-2 sm:flex-row-reverse flex-col cursor-pointer py-3"
          onClick={handleMainClick}
        >
          <time className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(date)}</time>
        </div>
      </div>
    </li>
  );
}

export function RecordingEntry({ onClick, recording }: { 
  onClick: () => void, 
  recording: Content
}) {
  const { toggleItem, isItemSelected } = useSelectedItemStore();
  
  return (
    <ListEntry
      content={recording}
      checked={isItemSelected(recording.id)}
      onCheckedChange={() => toggleItem(recording.id)}
      onClick={onClick}
    />
  );
}

export function PhotoEntry({ onClick, photo }: { 
  onClick: () => void, 
  photo: Content
}) {
  const { toggleItem, isItemSelected } = useSelectedItemStore();
  
  return (
    <ListEntry
      content={photo}
      checked={isItemSelected(photo.id)}
      onCheckedChange={() => toggleItem(photo.id)}
      onClick={onClick}
    />
  );
}